<script lang="ts" setup>
import { toRefs, ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { isBounce } from '@/lib/useServiceBounce';
import type { Menu } from '@/lib/constants/gnbMenuMap';
import { useAuthStore } from '@/stores/auth';

const props = defineProps<{
  menu: Menu[];
  mypage: Menu[];
  signOut: () => void;
  isAuthenticated: boolean;
}>();

const authStore = useAuthStore();
const router = useRouter();
const { menu, mypage, signOut, isAuthenticated } = toRefs(props);
const menuKey = ref<number | 'mypage'>();

const client = computed(() => authStore.clientInfo);
const center = computed(() => authStore.centerInfo);

const childrenRouter = computed(() => {
  if (menuKey.value === 'mypage') return mypage.value;
  return menu.value[menuKey.value!].children;
});

const childrenPosition = computed(() => {
  if (menuKey.value === 'mypage') return 'justify-end';
  return '';
});

/** 로그인 상태 우측 영역(이름 · 지점명) 최대 폭 — .auth-area의 max-width로 주입 */
const authAreaMaxWidth = '300px';

/** 상위메뉴 container — 하위메뉴 좌측 여백을 이 기준으로 잰다 */
const topMenuContainer = ref<HTMLElement>();
/** 마우스 올린 상위메뉴 글자의 좌측 위치(container 기준, px) */
const childrenPaddingLeft = ref(0);

/**
 * 상위메뉴 hover — 하위메뉴를 해당 메뉴 글자 왼쪽에 맞추기 위해 실제 위치를 측정한다.
 * 상·하위 메뉴가 같은 .container(동일 폭·중앙정렬)를 쓰므로 이 값을 그대로 padding-left로 쓸 수 있다.
 */
const onTopMenuHover = (index: number, event: MouseEvent) => {
  menuKey.value = index;

  const container = topMenuContainer.value;
  const label = (event.currentTarget as HTMLElement).firstElementChild;
  if (!container || !label) return;

  childrenPaddingLeft.value = label.getBoundingClientRect().left - container.getBoundingClientRect().left;
};

const childrenOffsetX = computed(() => {
  if (menuKey.value === 'mypage') return { right: '20px' };
  return { 'padding-left': `${childrenPaddingLeft.value}px` };
});

const isOpenGotcha = computed(() => {
  // const gotchaOpenCenter =
  // import.meta.env.VITE_ENV === "master"
  //   ? "62d501d545b113001cb20ed2"
  //   : "62a1729a6352c9001c985238";
  // return center.value.centerId === gotchaOpenCenter;
  return false;
});

const gotchaLink = 'https://bit.ly/3Fy5c8t';
const manualLink = 'https://www.dpost.blog/guide';

onMounted(() => {
  router.beforeEach((to, from) => {
    menuKey.value = undefined;
  });
});
</script>

<template>
  <div
    role="banner"
    aria-label="데스크탑 메뉴"
    class="h-20 transition mx-auto"
    @mouseleave="menuKey = undefined"
  >
    <!-- 메뉴 -->
    <div
      ref="topMenuContainer"
      class="container flex justify-between items-center h-full z-30"
    >
      <router-link to="/">
        <img
          class="logo"
          src="@/assets/image/dpost-logo.svg"
          alt="로고"
        />
      </router-link>

      <!-- 라우터 -->
      <nav
        class="router"
        role="navigation"
      >
        <router-link
          v-for="(item, index) in menu"
          :key="item.title"
          :to="item.link"
          class="router__item"
          @mouseover="onTopMenuHover(index, $event)"
          v-show="item.isRender !== false"
        >
          <div>
            {{ item.title }}
          </div>
        </router-link>
      </nav>

      <div class="flex items-center gap-4 text-xs font-semibold">
        <button
          @click.stop="
            router.push('/service');
            isBounce++;
          "
          class="faq-button"
        >
          상담문의
        </button>

        <div
          v-if="!isAuthenticated"
          class="flex"
        >
          <button
            @click="router.push('/signin')"
            class="flex flex-row gap-2.5"
          >
            로그인
            <div class="font-thin">|</div>
            회원가입
          </button>
        </div>

        <div
          v-else
          @mouseover="menuKey = 'mypage'"
          @click="router.push('/mypage')"
          class="auth-area flex items-center gap-2 cursor-pointer"
        >
          <div class="name-area flex items-center gap-1">
            <div class="client-name">{{ client.name }}</div>
            <div class="shrink-0">님</div>
          </div>
          <div class="font-thin shrink-0">|</div>
          <div class="center-area flex items-center gap-1">
            <div class="center-name">{{ center.name }}</div>
            <img
              class="shrink-0"
              src="@/assets/image/profile.svg"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- 하위메뉴 -->
    <div
      class="w-full bg-white border-y border-black2 h-16"
      v-if="typeof menuKey !== 'undefined' && childrenRouter?.length"
    >
      <nav
        role="navigation"
        class="container h-full flex gap-8"
        :class="`${childrenPosition}`"
        :style="childrenOffsetX"
      >
        <router-link
          v-for="(item, index) in childrenRouter"
          :key="index"
          :to="item.link"
          class="child-router__item"
        >
          {{ item.title }}
        </router-link>
        <a
          :href="gotchaLink"
          target="_blank"
          v-if="menuKey === 0 && isOpenGotcha"
          class="child-router__item"
          >세차접수</a
        >
        <a
          :href="manualLink"
          target="_blank"
          v-if="menuKey === 2"
          class="child-router__item"
          >이용가이드</a
        >

        <button
          v-if="menuKey === 'mypage'"
          @click="signOut()"
          class="hover:text-primary transition-all"
        >
          로그아웃
        </button>
      </nav>
    </div>
  </div>
</template>

<style lang="scss" scoped>
button {
  @apply bg-transparent h-auto;
  color: inherit;
}
.router {
  @apply h-full grid grid-cols-4 gap-10;

  &__item {
    @apply flex flex-col justify-center items-center;
    @apply text-lg font-semibold;
    @apply border-b-4 border-transparent pt-1;
    @apply hover:text-primary hover:transition;
  }
}

.child-router__item {
  @apply hover:text-primary font-semibold transition-all h-full flex flex-col justify-center;
}

a {
  &.router-link-exact-active {
    @apply text-primary;
  }
}

.faq-button {
  @apply bg-darkblue text-white px-5 py-2.5 rounded;
}

/** 로그인 상태 우측 영역(이름 · 지점명) 전체 폭 상한 */
.auth-area {
  max-width: v-bind(authAreaMaxWidth);
}

/**
 * 이름 영역 : 지점명 영역 = 1.5 : 2.
 * flex-basis를 0으로 두고 grow 비율로 나누되, max-width: max-content로 내용보다 넓어지지 않게 막는다.
 * → 둘 다 길면 정확히 1.5:2로 나뉘고, 한쪽이 짧으면 남는 폭이 반대쪽으로 넘어간다.
 */
.name-area,
.center-area {
  min-width: 0;
  max-width: max-content;
}
.name-area {
  flex: 1.5 1.5 0;
}
.center-area {
  flex: 2 2 0;
}

/**
 * 이름·지점명 — 할당된 폭을 넘으면 2줄로 접고, 2줄도 넘치면 말줄임(…).
 * 여러 줄 말줄임은 text-overflow로 불가능해 -webkit-line-clamp를 사용한다.
 */
.client-name,
.center-name {
  min-width: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  /* 공백 없는 한글 이름·지점명도 폭에 맞춰 끊기도록 글자 단위 줄바꿈 */
  word-break: break-all;
}
</style>
